import { CustomHeader } from "@/components/CustomHeader";
import { GroupAvatar } from "@/components/GroupAvatar.web";
import { ImagePreviewModal } from "@/components/ImagePreviewModal.web";
import { PostCard } from "@/components/PostCard";
import { RequestGroupAccess } from "@/components/RequestGroupAccess";
import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import { Fab } from "@/components/ui/Fab.web";
// Use web-specific Firebase SDK modules on web
import { firestore } from "@/firebase/web";
import { Group } from "@/types/group";
import { Post } from "@/types/post";
import { getGroupPictureOriginal } from "@/utils/getGroupPictureOriginal";
// Kept: the preview-modal URL fallback still consults the override map
// (see JSX below). GroupAvatar consults the same map internally.
import { useGroupImageOverride } from "@/utils/groupImageOverrides";
import { mapDocToPost } from "@/utils/mapDocToPost.web";
import { cachePosts } from "@/utils/postCache";
import {
  doc,
  collection as firestoreCollection,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
// Removed ActionSheetProvider for web compatibility
import { useGroupAccess } from "@/hooks/useGroupAccess";
import { useMarkGroupViewed } from "@/hooks/useMarkGroupViewed";
import { useThemeColor } from "@/hooks/useThemeColor";
import { useLocalSearchParams, useRouter } from "expo-router";
import Head from "expo-router/head";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { APP_NAME } from "@/utils/appMeta";

import {
  ActivityIndicator,
  FlatList,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";
const DefaultGroupImage = require("../../../assets/images/default-group.webp");

const TAB_BAR_HEIGHT = 40;

export default function GroupDetailsScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const [group, setGroup] = useState<Group | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);
  const { t } = useTranslation();
  const stripesColor = useThemeColor({}, "stripes");
  const backgroundColor = useThemeColor({}, "background");
  const headerForeground = useThemeColor({}, "tabIconActive");
  const { status: accessStatus, canRead, canPost } = useGroupAccess(id);
  const blockedFromGroup = accessStatus !== "loading" && !canRead;
  const overrideUri = useGroupImageOverride(id);

  useMarkGroupViewed(id);

  useEffect(() => {
    if (!id) return;

    const groupDocRef = doc(firestore, "groups", id); // Web Firebase syntax
    const unsubscribeGroup = onSnapshot(groupDocRef, (doc) => {
      // Web Firebase syntax
      if (doc.exists()) {
        setGroup({ id: doc.id, ...doc.data() } as Group);
      } else {
        // Handle group not found
      }
    });

    const postsQuery = query(
      // Web Firebase syntax
      firestoreCollection(firestore, "posts"),
      where("groupId", "==", id),
      orderBy("createdAt", "desc"),
    );
    const unsubscribePosts = onSnapshot(postsQuery, (snapshot) => {
      // Web Firebase syntax
      const postsData = snapshot.docs.map((doc) => mapDocToPost(doc));
      setPosts(postsData);
      // Seed the by-id cache so tapping a group post repaints details instantly.
      cachePosts(postsData);
      setLoading(false);
    });

    return () => {
      unsubscribeGroup();
      unsubscribePosts();
    };
  }, [id]);

  const docTitle = group?.name
    ? t("PAGE_TITLE_GROUP", { name: group.name })
    : APP_NAME;

  return (
    <>
      <Head>
        <title>{docTitle}</title>
      </Head>
      {" "}
      {/* Removed ActionSheetProvider */}
      {/* CustomHeader lives outside the maxWidth:680 container so it can
          span the full screen width; the posts feed below stays centered
          in the 680 column. */}
      <TouchableOpacity
        testID="group-details-header"
        disabled={!canPost}
        onPress={() =>
          router.push({
            pathname: "/(tabs)/groups/group-info",
            params: { group: JSON.stringify(group) },
          })
        }
      >
        <CustomHeader type="subtitle" variant="tab" showBackButton>
          <View style={styles.headerContent}>
            <TouchableOpacity
              testID="group-avatar-button"
              style={styles.avatarTouchable}
              onPress={(e) => {
                e.preventDefault?.();
                e.stopPropagation?.();
                setPreviewOpen(true);
              }}
            >
              <GroupAvatar
                groupId={id}
                photoUpdatedAt={group?.photoUpdatedAt}
              />
            </TouchableOpacity>
            <ThemedText
              testID="group-name"
              type="subtitle"
              numberOfLines={1}
              ellipsizeMode="tail"
              style={{ color: headerForeground }}
            >
              {group?.name ?? ""}
            </ThemedText>
          </View>
        </CustomHeader>
      </TouchableOpacity>
      <ThemedView
        style={{ flex: 1, backgroundColor: stripesColor, alignItems: "center" }}
      >
        <ThemedView style={[styles.container, { backgroundColor }]}>
          {accessStatus === "loading" || (loading && !blockedFromGroup) ? (
            <ThemedView style={styles.centered}>
              <ActivityIndicator />
            </ThemedView>
          ) : blockedFromGroup ? (
            <RequestGroupAccess
              groupId={id}
              groupName={group?.name ?? ""}
              pending={accessStatus === "request-pending"}
            />
          ) : (
            <FlatList
              data={posts}
              contentContainerStyle={
                posts.length === 0
                  ? styles.emptyListContentContainer
                  : styles.listContentContainer
              }
              keyExtractor={(item) => item.id}
              renderItem={({ item }) => <PostCard post={item} />}
              ListEmptyComponent={() => (
                <ThemedView
                  style={{
                    flex: 1,
                    width: "100%",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <ThemedText
                    testID="group-details-empty"
                    style={styles.emptyText}
                  >
                    {t("NO_POSTS_IN_GROUP")}
                  </ThemedText>
                </ThemedView>
              )}
              style={styles.postsList}
            />
          )}
          {canPost && (
            <Fab
              testID="group-details-fab"
              onPress={() =>
                router.push({
                  pathname: "/(tabs)/post/new",
                  params: {
                    groupId: id,
                    targetName: group?.name || t("GROUPS"),
                  },
                })
              }
            />
          )}
        </ThemedView>
      </ThemedView>
      <ImagePreviewModal
        visible={previewOpen}
        onClose={() => setPreviewOpen(false)}
        uri={
          overrideUri ??
          getGroupPictureOriginal(id, group?.photoUpdatedAt?.toMillis?.())
        }
        fallback={DefaultGroupImage}
        alt={group?.name}
      />
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: "100%",
    maxWidth: 680,
  },
  centered: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  headerContent: {
    flexDirection: "row",
    alignItems: "center",
  },
  avatarTouchable: {
    marginRight: 12,
  },
  postsList: {
    flex: 1,
    width: "100%",
  },
  emptyListContentContainer: {
    flexGrow: 1,
  },
  listContentContainer: {
    paddingBottom: TAB_BAR_HEIGHT,
  },
  emptyText: {
    color: "#666",
  },
});
